import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { Sun, Moon } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';
import GoogleLoginButton from '../components/GoogleLoginButton';

export default function Register() {
  const { register } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', age: '', gender: 'homme', looking_for: 'femme', referral_code: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register({ ...form, age: parseInt(form.age) });
      navigate('/discover');
    } catch (err) {
      setError(err.response?.data?.detail || t('common.error'));
    }
    setLoading(false);
  };

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{background:'var(--bg-primary)'}}>
      <button onClick={toggleTheme} className="fixed top-5 right-5 p-3 rounded-full glass-card-hover" style={{color:'var(--text-muted)'}} data-testid="theme-toggle">
        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
      </button>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="logo.png" alt="AHOOW" className="h-14 w-14 rounded-2xl mx-auto mb-4" />
          <h1 className="text-3xl font-black mb-2" style={{color:'var(--text-primary)'}}>{t('auth.register')}</h1>
          <p style={{color:'var(--text-muted)'}}>{t('auth.registerSubtitle')}</p>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-xl px-4 py-3 mb-5">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{color:'var(--text-muted)'}}>{t('profile.name')}</label>
            <input type="text" value={form.name} onChange={(e) => update('name', e.target.value)} className="input-field" required data-testid="register-name" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{color:'var(--text-muted)'}}>{t('auth.email')}</label>
            <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="input-field" required data-testid="register-email" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{color:'var(--text-muted)'}}>{t('auth.password')}</label>
            <PasswordInput value={form.password} onChange={(e) => update('password', e.target.value)} placeholder="Min. 6 caracteres" minLength={6} required data-testid="register-password" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{color:'var(--text-muted)'}}>{t('profile.age')}</label>
              <input type="number" value={form.age} onChange={(e) => update('age', e.target.value)} className="input-field" min={18} max={99} required data-testid="register-age" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{color:'var(--text-muted)'}}>{t('profile.gender')}</label>
              <select value={form.gender} onChange={(e) => update('gender', e.target.value)} className="input-field" data-testid="register-gender">
                <option value="homme">{t('profile.male')}</option>
                <option value="femme">{t('profile.female')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{color:'var(--text-muted)'}}>{t('profile.lookingFor')}</label>
              <select value={form.looking_for} onChange={(e) => update('looking_for', e.target.value)} className="input-field" data-testid="register-looking">
                <option value="femme">{t('profile.female')}</option>
                <option value="homme">{t('profile.male')}</option>
                <option value="les_deux">{t('profile.both')}</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{color:'var(--text-muted)'}}>{t('auth.referralCode')}</label>
            <input type="text" value={form.referral_code} onChange={(e) => update('referral_code', e.target.value)} className="input-field" placeholder={t('auth.referralCodePlaceholder')} data-testid="register-referral" />
          </div>
          <button type="submit" disabled={loading} className="w-full py-4 rounded-full bg-gradient-to-r from-brand-pink via-rose-500 to-brand-violet text-white font-bold disabled:opacity-50 hover:shadow-lg hover:shadow-brand-pink/30 transition-all" data-testid="register-submit">
            {loading ? t('common.loading') : t('auth.register')}
          </button>
        </form>

        <GoogleLoginButton onSuccess={() => navigate('/discover')} onError={(msg) => setError(msg)} />

        <p className="text-center text-sm mt-6" style={{color:'var(--text-muted)'}}>
          {t('auth.hasAccount')}{' '}
          <Link to="/login" className="text-brand-pink font-semibold">{t('auth.login')}</Link>
        </p>
      </div>
    </div>
  );
}
